import { Button } from 'antd'
import { CaretRightOutlined, LoadingOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { resolveLazyAudioPreload, resolveLazyAudioSrc } from './lazyAudioPlayback'

type PlaybackUiState = 'idle' | 'loading' | 'ready' | 'error'

type Props = {
  playbackUrl: string
  testId?: string
  onPlay?: (el: HTMLAudioElement) => void
  /** Defer assigning media src until the user clicks play (Canvas Generate Audio). */
  lazyLoad?: boolean
}

/**
 * Tenant-private audio on Preview is served from api-preview via same-site
 * session cookies. Do NOT fetch+blob here — cross-origin fetch is blocked by
 * CORS ("Failed to fetch") even when plain `<audio src>` works.
 */
export default function VoiceAudioPlayer({ playbackUrl, testId, onPlay, lazyLoad = false }: Props) {
  const { t } = useTranslation('virtualIp')
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [armed, setArmed] = useState(!lazyLoad)
  const [playbackUi, setPlaybackUi] = useState<PlaybackUiState>(lazyLoad ? 'idle' : 'ready')

  const reload = useCallback(() => {
    setPlaybackUi(lazyLoad && !armed ? 'idle' : 'loading')
    setAttempt((n) => n + 1)
    if (lazyLoad) setArmed(true)
  }, [armed, lazyLoad])

  const armAndPlay = useCallback(() => {
    setPlaybackUi('loading')
    setArmed(true)
  }, [])

  useEffect(() => {
    if (!lazyLoad || !armed) return
    const el = audioRef.current
    if (!el) return
    const handleCanPlay = () => setPlaybackUi('ready')
    const handleWaiting = () => setPlaybackUi('loading')
    const handlePlaying = () => setPlaybackUi('ready')
    el.addEventListener('canplay', handleCanPlay)
    el.addEventListener('waiting', handleWaiting)
    el.addEventListener('playing', handlePlaying)
    void el.play().catch(() => setPlaybackUi('error'))
    return () => {
      el.removeEventListener('canplay', handleCanPlay)
      el.removeEventListener('waiting', handleWaiting)
      el.removeEventListener('playing', handlePlaying)
    }
  }, [armed, lazyLoad, playbackUrl, attempt])

  if (playbackUi === 'error') {
    return (
      <div className="tc-vip-step7-audio-error" data-testid="vip-step7-candidate-audio-error">
        <span className="tc-vip-card-hint">{t('voiceAudio.loadFailed')}</span>
        <Button size="small" type="link" onClick={reload} data-testid="vip-step7-candidate-audio-retry">
          {t('voiceAudio.reload')}
        </Button>
      </div>
    )
  }

  const mediaSrc = resolveLazyAudioSrc({ lazyLoad, armed, playbackUrl })

  if (lazyLoad && !armed) {
    return (
      <div
        className="tc-voice-audio-lazy-idle nodrag nopan"
        data-testid={testId ? `${testId}-idle` : 'voice-audio-lazy-idle'}
        data-playback-state="idle"
      >
        <button
          type="button"
          className="tc-voice-audio-lazy-play"
          data-testid={testId ? `${testId}-play` : 'voice-audio-lazy-play'}
          onClick={(e) => {
            e.stopPropagation()
            armAndPlay()
          }}
        >
          <CaretRightOutlined aria-hidden />
          <span>{t('voiceAudio.play')}</span>
        </button>
      </div>
    )
  }

  return (
    <div className="tc-voice-audio-player-shell" data-playback-state={playbackUi}>
      {playbackUi === 'loading' ? (
        <div className="tc-voice-audio-playback-loading" data-testid={testId ? `${testId}-loading` : 'voice-audio-playback-loading'}>
          <LoadingOutlined spin aria-hidden />
          <span>{t('voiceAudio.playbackLoading')}</span>
        </div>
      ) : null}
      <audio
        key={`${playbackUrl}:${attempt}:${armed ? 'armed' : 'idle'}`}
        ref={audioRef}
        controls
        src={mediaSrc}
        preload={resolveLazyAudioPreload(lazyLoad)}
        data-testid={testId || 'vip-step7-candidate-player'}
        onPlay={(e) => {
          setPlaybackUi('ready')
          onPlay?.(e.currentTarget)
        }}
        onError={() => setPlaybackUi('error')}
      />
    </div>
  )
}
